
import React from 'react';
import { getInstitutions } from '../../../api/institutions';
import { getSeccionesAcademicas } from '../../../api/secciones_academicas';
import { getCampus } from '../../../api/campus';
import { getCycles } from '../../../api/ciclosEscolares';
import { getPrograms } from '../../../api/programs';
import { TURNO_OPTIONS, getTurnoLabel } from '../../../constants/turnos';
import { MODALIDAD_OPTIONS, getModalidadLabel } from '../../../constants/modalidades';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';


const InformacionEscolar = ({ values, onChange, student, mode = 'view' }) => {
  // Sincronizar valores iniciales al editar
  React.useEffect(() => {
    if (mode === 'edit' && student && onChange) {
      const cicloStudent = student.ciclo_ingreso_id || (student.ciclo_ingreso && student.ciclo_ingreso.id_ciclo_escolar);
      if (!values.ciclo_ingreso && cicloStudent) {
        onChange('ciclo_ingreso', String(cicloStudent));
      }
    }
  }, [mode, student, values.ciclo_ingreso, onChange]);
  const isEditable = mode === 'create' || mode === 'edit';
  const isEditMode = mode === 'edit';
  // Valores de solo lectura para mostrar cuando no es editable
  const programaValor = student.programa_nombre || student.programa || values?.programa || 'No disponible';
  // Estados locales para listas (declarados antes de usarse)
  const [instituciones, setInstituciones] = React.useState([]);
  const [secciones, setSecciones] = React.useState([]);
  const [campusList, setCampusList] = React.useState([]);
  const [ciclosList, setCiclosList] = React.useState([]);
  const [programas, setProgramas] = React.useState([]);
  const prevSeccionRef = React.useRef(values.seccion);

  // Prioriza ciclo_ingreso_id si existe
  // Sincroniza ciclo_escolar en modo edición si está vacío
  React.useEffect(() => {
    if (isEditMode && student && !values.ciclo_ingreso) {
      const cicloId = student.ciclo_ingreso_id || (student.ciclo_ingreso && student.ciclo_ingreso.id_ciclo_escolar);
      if (cicloId) {
        onChange('ciclo_ingreso', String(cicloId));
      }
    }
  }, [isEditMode, student, values.ciclo_ingreso, onChange]);
  // Mostrar nombre del ciclo ACTUAL del estudiante (derivado de su inscripcion/reinscripcion vigente).
  // Backend expone `ciclo_actual` (objeto) y `ciclo_actual_nombre` (string) en el serializer del estudiante.
  // En modo edicion/creacion seguimos usando `ciclo_ingreso` como valor del SELECT (campo persistido del modelo).
  const selectedCicloId = values?.ciclo_ingreso || student.ciclo_ingreso_id || '';
  const cicloFromList = ciclosList.find(c => String(c.id) === String(selectedCicloId));
  const cicloIngresoNombre = (cicloFromList && (cicloFromList.name || cicloFromList.nombre)) || student.ciclo_escolar_nombre || '';
  const cicloActualNombre =
    student.ciclo_actual_nombre ||
    (student.ciclo_actual && student.ciclo_actual.nombre_ciclo) ||
    '';
  const cicloValor = (isEditable ? cicloIngresoNombre : (cicloActualNombre || cicloIngresoNombre)) || 'No disponible';
  const matriculaValor = values?.matricula || student.matricula || 'No disponible';
  const promedioGeneralValor =
  values?.promedio_general ||
  student.promedio_procedencia ||
  student.promedio_general ||
  student.informacionEscolar?.promedioGeneral ||
  'N/A';

  const seccionValor = student.seccion_nombre || student.informacionEscolar?.seccion || student.seccion || values?.seccion || '';
  const institucionValor = student.institucion_nombre || student.informacionEscolar?.institucion || values?.institucion || '';
  // Periodo actual: preferir periodo_estudiante derivado de la inscripcion vigente,
  // luego materias_ciclo_actual.ciclo, luego dato local del front.
  const periodoActualValor =
    (student.ciclo_actual && student.ciclo_actual.periodo_estudiante) ||
    student.materias_ciclo_actual?.ciclo ||
    student.informacionEscolar?.periodoActual ||
    'No disponible';
  const campusValor = student.campus_nombre || student.campus || values?.campus || '';


  // DEPURACIÓN: Mostrar valor y lista de ciclos escolares
  React.useEffect(() => {
    console.log('[CICLO ESCOLAR] Valor actual:', values.ciclo_ingreso, typeof values.ciclo_ingreso);
    const ids = ciclosList.map(c => String(c.id));
    console.log('[CICLO ESCOLAR] Lista de ids:', ids);
    if (values.ciclo_ingreso) {
      const existe = ids.includes(String(values.ciclo_ingreso));
      console.log('[CICLO ESCOLAR] ¿Existe el valor en la lista?:', existe);
      if (!existe) {
        console.warn('[CICLO ESCOLAR] El valor actual no coincide con ningún id de la lista');
      }
    }
  }, [values.ciclo_ingreso, ciclosList]);
  React.useEffect(() => {
    if (!isEditable) return;
    getInstitutions().then(data => setInstituciones(data)).catch(() => setInstituciones([]));
    getSeccionesAcademicas().then(data => setSecciones(data)).catch(() => setSecciones([]));
    getCampus().then(data => setCampusList(data)).catch(() => setCampusList([]));
    getCycles().then(data => setCiclosList(data)).catch(() => setCiclosList([]));
    getPrograms().then(data => setProgramas(data)).catch(() => setProgramas([]));
  }, [isEditable]);

  // Si cargó la lista de ciclos y no tenemos id en el formulario,
  // intentar emparejar por nombre (caso donde el backend devuelve solo el nombre)
  React.useEffect(() => {
    if (!isEditable) return;
    if (Array.isArray(ciclosList) && ciclosList.length > 0 && !values.ciclo_ingreso) {
      const posibleNombres = [
        student.ciclo_escolar_nombre,
        student.ciclo_escolar,
        student.ciclo_ingreso?.nombre_ciclo,
        student.ciclo_ingreso_nombre,
        student.cicloEscolar,
      ].filter(Boolean).map(s => String(s).toLowerCase());

      if (posibleNombres.length === 0) return;

      const encontrado = ciclosList.find(c => {
        const name = (c.name || c.nombre || c.nombre_ciclo || '').toString().toLowerCase();
        return posibleNombres.includes(name);
      });

      if (encontrado) {
        // Setear el id en el formulario usando onChange para mantener sincronía con el modal
        onChange && onChange('ciclo_ingreso', String(encontrado.id));
      }
    }
  }, [ciclosList, values.ciclo_ingreso, isEditable, student, onChange]);

  // Filtrar programas según la sección seleccionada
  const programasFiltrados = React.useMemo(() => {
    if (!values.seccion) return [];
    return programas.filter(p => p.sectionId !== null && String(p.sectionId) === String(values.seccion));
  }, [programas, values.seccion]);

  // Resetear programa cuando cambia la sección
  React.useEffect(() => {
    if (!isEditable) return;
    if (prevSeccionRef.current !== values.seccion) {
      prevSeccionRef.current = values.seccion;
      // Solo resetear si el programa actual no pertenece a la nueva sección
      if (values.programa) {
        const perteneceASeccion = programasFiltrados.some(p => String(p.id) === String(values.programa));
        if (!perteneceASeccion) {
          onChange && onChange('programa', '');
        }
      }
    }
  }, [values.seccion, values.programa, programasFiltrados, isEditable, onChange]);

  // Sincronizar promedio_general: tomar de student.promedio_procedencia o student.promedio_general
  React.useEffect(() => {
    if (!isEditable) return;
    if (values.promedio_general && values.promedio_general !== '') return;
    const candidato = student.promedio_procedencia || student.promedio_general || student.informacionEscolar?.promedioGeneral;
    if (candidato !== undefined && candidato !== null && candidato !== '') {
      onChange && onChange('promedio_general', String(candidato));
    }
  }, [isEditable, values.promedio_general, student, onChange]);

  // Sincronizar institución: si tenemos un valor en el formulario que no coincide
  // con ningún id de la lista, intentar emparejar por nombre o clave y reemplazar
  React.useEffect(() => {
    if (!isEditable) return;
    if (!Array.isArray(instituciones) || instituciones.length === 0) return;

    const ids = instituciones.map(i => String(i.id));
    // Si ya está un id válido, nada que hacer
    if (values.institucion && ids.includes(String(values.institucion))) return;

    // Construir posibles nombres/clave a partir del student o del valor actual
    const posibles = [
      student.institucion_nombre,
      student.institucion,
      student.informacionEscolar?.institucion,
      values.institucion,
    ].filter(Boolean).map(s => String(s).toLowerCase());

    if (posibles.length === 0) return;

    const encontrado = instituciones.find(inst => {
      const name = (inst.name || '').toString().toLowerCase();
      const clave = (inst.clave || '').toString().toLowerCase();
      return posibles.includes(name) || posibles.includes(clave) || posibles.includes(String(inst.id).toLowerCase());
    });

    if (encontrado) {
      onChange && onChange('institucion', String(encontrado.id));
    }
  }, [instituciones, values.institucion, isEditable, student, onChange]);

  // Sincronizar programa (id) desde student si falta o si el valor actual no es un id válido
  React.useEffect(() => {
    if (!isEditable) return;
    if (!Array.isArray(programas) || programas.length === 0) return;

    const ids = programas.map(p => String(p.id));
    const hasValidId = values.programa && ids.includes(String(values.programa));
    if (hasValidId) return; // ya está bien

    // Intentar emparejar por nombre a partir de student o del valor actual
    const posibles = [
      values.programa,
      student.programa,
      student.programa_id,
      student.programa_nombre,
      student.id_programa_actual,
      student.id_programa_actual_id
    ].filter(Boolean).map(s => String(s).toLowerCase());

    if (posibles.length === 0) return;

    // Solo buscar dentro de los programas de la sección seleccionada (o en todos si no hay sección)
    const pool = values.seccion
      ? programas.filter(p => p.sectionId !== null && String(p.sectionId) === String(values.seccion))
      : programas;

    const found = pool.find(p => {
      const name = (p.name || p.nombre || p.nombre_programa || '').toString().toLowerCase();
      return posibles.includes(name) || posibles.includes(String(p.id).toLowerCase());
    });

    if (found) onChange && onChange('programa', String(found.id));
  }, [programas, values.programa, values.seccion, isEditable, student, onChange]);

  // Sincronizar seccion (id) desde student si falta
  React.useEffect(() => {
    if (!isEditable) return;
    if (Array.isArray(secciones) && secciones.length > 0 && !values.seccion) {
      const possible = [student.seccion, student.seccion_id, student.id_seccion, student.seccion_nombre, student.informacionEscolar?.seccion].filter(Boolean).map(s => String(s).toLowerCase());
      const found = secciones.find(s => {
        const name = (s.nombre || s.name || '').toString().toLowerCase();
        return possible.includes(name) || possible.includes(String(s.id).toLowerCase());
      });
      if (found) onChange && onChange('seccion', String(found.id));
    }
  }, [secciones, values.seccion, isEditable, student, onChange]);

  // Sincronizar campus (id) desde student si falta -> intentar emparejar por nombre/clave
  React.useEffect(() => {
    if (!isEditable) return;
    if (Array.isArray(campusList) && campusList.length > 0 && !values.campus) {
      const posibles = [
        student.campus_nombre,
        student.campus,
        student.campus_id,
        student.id_campus,
        student.id_campus_id,
        student.informacionEscolar?.campus
      ].filter(Boolean).map(s => String(s).toLowerCase());

      if (posibles.length === 0) return;

      const encontrado = campusList.find(c => {
        const name = (c.nombre || c.name || '').toString().toLowerCase();
        return posibles.includes(name) || posibles.includes(String(c.id).toLowerCase());
      });

      if (encontrado) {
        onChange && onChange('campus', String(encontrado.id));
      }
    }
  }, [campusList, values.campus, isEditable, student, onChange]);

  // Depuración y emparejado cuando hay un valor presente pero no coincide con la lista de campus
  React.useEffect(() => {
    if (!isEditable) return;
    if (!Array.isArray(campusList) || campusList.length === 0) return;

    const ids = campusList.map(c => String(c.id));
    console.log('[CAMPUS] student campus fields:', {
      id_campus_id: student.id_campus_id,
      id_campus_obj: student.id_campus,
      campus: student.campus,
      campus_nombre: student.campus_nombre,
      valuesCampus: values.campus
    });
    console.log('[CAMPUS] campusList ids:', ids);

    // Si ya tenemos un id válido en el formulario pero no existe en la lista,
    // intentar emparejar por nombre y reemplazar por el id correcto.
    if (values.campus && !ids.includes(String(values.campus))) {
      const posibles = [student.campus_nombre, student.campus, student.id_campus, student.id_campus_id, student.informacionEscolar?.campus].filter(Boolean).map(s => String(s).toLowerCase());
      if (posibles.length === 0) return;

      const encontradoPorNombre = campusList.find(c => {
        const name = (c.nombre || c.name || '').toString().toLowerCase();
        return posibles.includes(name) || posibles.includes(String(c.id).toLowerCase());
      });

      if (encontradoPorNombre) {
        console.log('[CAMPUS] Reemplazando values.campus por id encontrado:', encontradoPorNombre.id);
        onChange && onChange('campus', String(encontradoPorNombre.id));
      } else {
        console.warn('[CAMPUS] No se encontró emparejamiento por nombre para el campus existente en values.campus');
      }
    }
  }, [campusList, values.campus, isEditable, student, onChange]);

  // Sincronizar tipo_estudiante: mapear nombres legibles a las claves esperadas
  React.useEffect(() => {
    if (!isEditable) return;
    const valid = ['NUEVO_INGRESO','REINCORPORADO','PROMOVIDO','REPETIDOR','EGRESADO'];
    if (values.tipo_estudiante && valid.includes(String(values.tipo_estudiante))) return;

    const mapDisplayToKey = {
      'nuevo ingreso': 'NUEVO_INGRESO',
      'nuevo_ingreso': 'NUEVO_INGRESO',
      'reincorporado': 'REINCORPORADO',
      'promovido': 'PROMOVIDO',
      'repetidor': 'REPETIDOR',
      'egresado': 'EGRESADO'
    };

    const candidate = (student.tipo_estudiante || student.tipoEstudiante || values.tipo_estudiante || '').toString().trim();
    console.log('[TIPO ESTUDIANTE] current values.tipo_estudiante:', values.tipo_estudiante, 'student.tipo_estudiante:', student.tipo_estudiante, 'candidate:', candidate);
    if (!candidate) return;
    const lower = candidate.toLowerCase();
    const mapped = mapDisplayToKey[lower] || (valid.includes(candidate) ? candidate : null);
    console.log('[TIPO ESTUDIANTE] mapped:', mapped);
    if (mapped) {
      onChange && onChange('tipo_estudiante', mapped);
      console.log('[TIPO ESTUDIANTE] set via onChange ->', mapped);
    }
  }, [values.tipo_estudiante, student, isEditable, onChange]);

  // Puedes usar ref en el input principal si lo necesitas
  return (
    <div className={styles.tabPanel}>
      {/* Información Académica */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 14L21 9L12 4L3 9L12 14Z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 14L21 9V16" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 11.5V16.5C6 16.5 9 19 12 19C15 19 18 16.5 18 16.5V11.5" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3 className={styles.sectionTitle}>Información Académica</h3>
        </div>
        <div className={styles.academicCard}>
          <div className={styles.academicHeader}>
            {isEditable && (
              <span style={{ fontSize: '11px', color: '#fca5a5', fontStyle: 'italic' }}>
                Los campos marcados con (*) son obligatorios
              </span>
            )}
          </div>
          <div className={styles.academicGrid}>
            {/* 1. Matrícula */}
            <div className={styles.academicItem}>
              <span className={isEditable && !isEditMode ? styles.requiredFieldLabel : styles.academicLabel}>
                Matrícula:{isEditMode && ' (no editable)'}
              </span>
              {isEditable ? (
                <input
                  className={styles.studentFormInput}
                  type="text"
                  value={values.matricula}
                  onChange={e => onChange('matricula', e.target.value)}
                  placeholder="Matrícula"
                  maxLength={6}
                  required={!isEditMode}
                  disabled={isEditMode}
                  style={isEditMode ? { backgroundColor: '#e5e5e5', cursor: 'not-allowed' } : {}}
                />
              ) : (
                <span className={styles.academicValue}>{matriculaValor}</span>
              )}
            </div>
            {/* 2. Promedio General */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Promedio General:</span>
              {isEditable ? (
                <input
                  className={styles.studentFormInput}
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={values.promedio_general}
                  onChange={e => onChange('promedio_general', e.target.value)}
                  placeholder="Promedio general"
                  inputMode="decimal"
                  pattern="^[0-9]*[.,]?[0-9]*$"
                />
              ) : (
                <span className={styles.academicValue}>{promedioGeneralValor}</span>
              )}
            </div>
            {/* 3. Ciclo Escolar */}
            <div className={styles.academicItem}>
              <span className={isEditable ? styles.requiredFieldLabel : styles.academicLabel}>Ciclo Escolar de Ingreso:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.ciclo_ingreso ? String(values.ciclo_ingreso) : ''}
                  onChange={e => onChange('ciclo_ingreso', e.target.value)}
                  name="ciclo_ingreso"
                  required
                  disabled={mode === 'edit'}
                  title={mode === 'edit' ? 'El ciclo de ingreso es un valor histórico inmutable' : ''}
                >
                  <option value="">— Selecciona un ciclo escolar —</option>
                  {Array.isArray(ciclosList) && ciclosList.length > 0 ? (
                    ciclosList
                      .slice()
                      .sort((a, b) => {
                        // Extraer año y periodo de nombre tipo '2026-04' o '2026-1'
                        const parseCiclo = (str) => {
                          const match = (str || '').match(/(\d{4})-(\d+)/);
                          if (!match) return { year: 0, period: 0 };
                          return { year: parseInt(match[1], 10), period: parseInt(match[2], 10) };
                        };
                        const nameA = a.name || a.nombre || '';
                        const nameB = b.name || b.nombre || '';
                        const cicloA = parseCiclo(nameA);
                        const cicloB = parseCiclo(nameB);
                        if (cicloA.year !== cicloB.year) return cicloA.year - cicloB.year;
                        return cicloA.period - cicloB.period;
                      })
                      .map(c => (
                        <option key={c.id} value={String(c.id)}>{c.name || c.nombre}</option>
                      ))
                  ) : (
                    <option value="" disabled>Cargando ciclos escolares...</option>
                  )}
                </select>
              ) : (
                <span className={styles.academicValue}>{cicloValor}</span>
              )}
            </div>
            {/* 4. Programa Académico */}
            {(() => {
              // Programa incompatible: hay sección y programa seleccionados, la lista cargó,
              // y el programa actual NO pertenece a la sección seleccionada.
              const programaIncompatible = isEditable && values.seccion && values.programa && programas.length > 0
                && !programasFiltrados.some(p => String(p.id) === String(values.programa));
              // Objeto completo del programa actual (para mostrar su nombre aunque no esté en la lista filtrada)
              const programaActualObj = programaIncompatible
                ? programas.find(p => String(p.id) === String(values.programa))
                : null;
              return (
                <div className={styles.academicItem}>
                  <span className={isEditable ? styles.requiredFieldLabel : styles.academicLabel}>
                    Programa Académico:
                    {programaIncompatible && (
                      <span style={{ marginLeft: 6, fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>
                        ⚠ No corresponde a la sección
                      </span>
                    )}
                  </span>
                  {isEditable ? (
                    <select
                      className={styles.studentFormInput}
                      value={values.programa}
                      onChange={e => onChange('programa', e.target.value)}
                      name="programa"
                      required
                      disabled={!values.seccion}
                      style={
                        !values.seccion
                          ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed', color: '#999' }
                          : programaIncompatible
                          ? { borderColor: '#dc2626', boxShadow: '0 0 0 2px rgba(220,38,38,0.2)', backgroundColor: '#fff5f5' }
                          : {}
                      }
                    >
                      {!values.seccion ? (
                        <option value="">— Selecciona primero una sección —</option>
                      ) : (
                        <>
                          <option value="">— Selecciona un programa académico —</option>
                          {/* Si el programa actual no pertenece a la sección, mostrarlo como opción visible (con su nombre real) */}
                          {programaIncompatible && programaActualObj && (
                            <option value={programaActualObj.id}>
                              ⚠ {programaActualObj.name}
                            </option>
                          )}
                          {/* Opciones válidas para la sección seleccionada */}
                          {programasFiltrados.length > 0 ? (
                            programasFiltrados.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))
                          ) : (
                            <option value="" disabled>No hay programas para esta sección</option>
                          )}
                        </>
                      )}
                    </select>
                  ) : (
                    programaValor
                  )}
                </div>
              );
            })()}
            {/* 5. Sección */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Sección:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.seccion || ''}
                  onChange={e => onChange('seccion', e.target.value)}
                  name="seccion"
                >
                  <option value="">— Selecciona una sección —</option>
                  {Array.isArray(secciones) && secciones.length > 0 ? (
                    secciones.map(s => (
                      <option key={s.id} value={s.id}>{s.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando secciones...</option>
                  )}
                </select>
              ) : (
                <span className={styles.academicValue}>{seccionValor || 'No disponible'}</span>
              )}
            </div>
            {/* 6. Institución */}
            <div className={styles.academicItem}>
              <span className={isEditable ? styles.requiredFieldLabel : styles.academicLabel}>Institución:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.institucion || ''}
                  onChange={e => onChange('institucion', e.target.value)}
                  name="institucion"
                  required
                >
                  <option value="">— Selecciona una institución —</option>
                  {Array.isArray(instituciones) && instituciones.length > 0 ? (
                    instituciones.map(inst => (
                      <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando instituciones...</option>
                  )}
                </select>
              ) : (
                <span className={styles.academicValue}>{institucionValor || 'No disponible'}</span>
              )}
            </div>
            {/* 7. Campus */}
            <div className={styles.academicItem}>
              <span className={isEditable ? styles.requiredFieldLabel : styles.academicLabel}>Campus:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.campus || ''}
                  onChange={e => onChange('campus', e.target.value)}
                  name="campus"
                  required
                >
                  <option value="">— Selecciona un campus —</option>
                  {Array.isArray(campusList) && campusList.length > 0 ? (
                    campusList.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))
                  ) : (
                    <option value="" disabled>Cargando campus...</option>
                  )}
                </select>
              ) : (
                <span className={styles.academicValue}>{campusValor || 'No disponible'}</span>
              )}
            </div>
            {/* 8. Estatus del Estudiante */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Estatus:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.estatus_estudiante || ''}
                  onChange={e => onChange('estatus_estudiante', e.target.value)}
                  name="estatus_estudiante"
                >
                  <option value="">— Selecciona un estatus —</option>
                  <option value="ACTIVO">Activo</option>
                  <option value="BAJA_TEMPORAL">Baja Temporal</option>
                  <option value="BAJA_DEFINITIVA">Baja Definitiva</option>
                  <option value="EGRESADO">Egresado</option>
                  <option value="NO_INSCRITO">No Inscrito</option>
                  <option value="PASANTE">Pasante</option>
                </select>
              ) : (
                <span className={styles.academicValue}>
                  {student.estatus_estudiante || values.estatus_estudiante || 'No disponible'}
                </span>
              )}
            </div>
            {/* 9. Tipo de Estudiante */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Tipo de Estudiante:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.tipo_estudiante || ''}
                  onChange={e => onChange('tipo_estudiante', e.target.value)}
                  name="tipo_estudiante"
                >
                  <option value="">— Selecciona un tipo —</option>
                  <option value="NUEVO_INGRESO">Nuevo Ingreso</option>
                  <option value="REINCORPORADO">Reincorporado</option>
                  <option value="PROMOVIDO">Promovido</option>
                  <option value="REPETIDOR">Repetidor</option>
                  <option value="EGRESADO">Egresado</option>
                </select>
              ) : (
                <span className={styles.academicValue}>
                  {(() => {
                    const tipoMap = {
                      'NUEVO_INGRESO': 'Nuevo Ingreso',
                      'REINCORPORADO': 'Reincorporado',
                      'PROMOVIDO': 'Promovido',
                      'REPETIDOR': 'Repetidor',
                      'EGRESADO': 'Egresado'
                    };
                    return tipoMap[student.tipo_estudiante || values.tipo_estudiante] || 'No disponible';
                  })()}
                </span>
              )}
            </div>
            {/* 10. Turno */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Turno:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.turno || ''}
                  onChange={e => onChange('turno', e.target.value)}
                  name="turno"
                >
                  <option value="">— Selecciona un turno —</option>
                  {TURNO_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className={styles.academicValue}>
                  {getTurnoLabel(student.turno || values.turno, 'No disponible')}
                </span>
              )}
            </div>
            {/* 11. Modalidad */}
            <div className={styles.academicItem}>
              <span className={styles.academicLabel}>Modalidad:</span>
              {isEditable ? (
                <select
                  className={styles.studentFormInput}
                  value={values.modalidad || ''}
                  onChange={e => onChange('modalidad', e.target.value)}
                  name="modalidad"
                >
                  <option value="">— Selecciona una modalidad —</option>
                  {MODALIDAD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className={styles.academicValue}>
                  {getModalidadLabel(student.modalidad || values.modalidad, 'No disponible')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Matrícula Actual */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#ed8b00" strokeWidth="2"/>
            <path d="M9 3V21M15 3V21M3 9H21M3 15H21" stroke="#ed8b00" strokeWidth="2"/>
          </svg>
          <h3 className={styles.sectionTitle}>
            Matrícula Actual
            {student.materias_ciclo_actual?.ciclo && (
              <span style={{ fontWeight: 400, fontSize: '0.85em', marginLeft: '0.5rem', color: '#666' }}>
                — Ciclo: {student.materias_ciclo_actual.ciclo}
              </span>
            )}
          </h3>
        </div>
        <div className={styles.materiasTable}>
          <table>
            <thead>
              <tr>
                <th>Materia</th>
                <th>Código</th>
                <th>Créditos</th>
                <th>Grupo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {student.materias_ciclo_actual?.materias && student.materias_ciclo_actual.materias.length > 0 ? (
                student.materias_ciclo_actual.materias.map((materia, index) => (
                  <tr key={index}>
                    <td>{materia.materia || 'N/A'}</td>
                    <td>{materia.codigo || 'N/A'}</td>
                    <td style={{ textAlign: 'center' }}>{materia.creditos ?? 'N/A'}</td>
                    <td>{materia.grupo || 'N/A'}</td>
                    <td>
                      <span className={styles.estadoBadge}>{materia.estado || 'INSCRITO'}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    {student.materias_ciclo_actual?.mensaje || 'No hay materias registradas en este ciclo'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historial de Grupos Asignados */}
      <div className={styles.infoSection}>
        <div className={styles.sectionHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8V12L15 15" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="9" stroke="#ed8b00" strokeWidth="2"/>
          </svg>
          <h3 className={styles.sectionTitle}>
            Historial de Grupos Asignados
            {student.historial_grupos?.total > 0 && (
              <span style={{ fontWeight: 400, fontSize: '0.85em', marginLeft: '0.5rem', color: '#666' }}>
                ({student.historial_grupos.total} {student.historial_grupos.total === 1 ? 'registro' : 'registros'})
              </span>
            )}
          </h3>
        </div>
        <div className={styles.materiasTable}>
          <table>
            <thead>
              <tr>
                <th>Materia</th>
                <th>Código</th>
                <th>Créditos</th>
                <th>Grupo</th>
                <th>Ciclo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {student.historial_grupos?.grupos && student.historial_grupos.grupos.length > 0 ? (
                student.historial_grupos.grupos.map((grupo, index) => (
                  <tr key={index}>
                    <td>{grupo.materia || 'N/A'}</td>
                    <td>{grupo.codigo_materia || 'N/A'}</td>
                    <td style={{ textAlign: 'center' }}>{grupo.creditos ?? 'N/A'}</td>
                    <td>{grupo.grupo || 'N/A'}</td>
                    <td>{grupo.ciclo || 'N/A'}</td>
                    <td>
                      <span className={styles.estadoBadge}>{grupo.estado || 'N/A'}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    {student.historial_grupos?.mensaje || 'No hay historial de grupos disponible'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InformacionEscolar;
